import {
  DEFAULT_LANGUAGE,
  LANGUAGE_OPTIONS,
  getLanguageMarket,
  normalizeLanguage
} from '../locales/index.js';
import { APP_ROUTE_META_BY_PATH, getAppRouteAvailableLanguages } from '../data/appRouteSeo.js';
import {
  BLOG_PARTIAL_TRANSLATION_LANGUAGES,
  getStaticBlogTranslationLanguages
} from '../data/blogSlugs.js';
import { METADATA_SETTINGS_GUIDE_LANGUAGES } from '../data/helpGuides/metadataSettingsGuideLocales.js';
import { ENGLISH_ONLY_TOOL_LANDING_PATHS } from '../data/toolLandingAvailability.js';

const SUPPORTED_LANGUAGES = [
  DEFAULT_LANGUAGE,
  ...LANGUAGE_OPTIONS
    .map((option) => normalizeLanguage(option.code))
    .filter((code) => code && code !== DEFAULT_LANGUAGE)
];

const SUPPORTED_LANGUAGE_SET = new Set(SUPPORTED_LANGUAGES);
const SEO_PATH_LANGUAGE_OVERRIDES = new Map([
  // Same availability the sitemap, prerender and hreflang publish for these posts
  // (see BLOG_PARTIAL_TRANSLATION_LANGUAGES), so links and client routing never
  // move a visitor off a prerendered translation or onto a missing one.
  ...Object.entries(BLOG_PARTIAL_TRANSLATION_LANGUAGES).map(([slug, languages]) => [
    `/blog/${slug}`,
    new Set([DEFAULT_LANGUAGE, ...languages])
  ]),
  ['/help/metadata-settings-guide', new Set(METADATA_SETTINGS_GUIDE_LANGUAGES)],
  ['/about', new Set(['en'])],
  ['/editorial-policy', new Set(['en'])],
  ['/contact', new Set(['en'])],
  // English-only tool landings resolve to the English URL in every language.
  ...ENGLISH_ONLY_TOOL_LANDING_PATHS.map((pathname) => [pathname, new Set(['en'])])
]);
const LOCALE_ROUTED_STATIC_PATHS = new Set([
  '/',
  '/blog',
  '/privacy',
  '/terms',
  '/about',
  '/editorial-policy',
  '/contact',
  '/alternatives',
  '/metadata',
  '/market-intelligence',
  '/free-credits',
  '/image-generator',
  '/vectorizer',
  '/prompter',
  '/pricing',
  '/feedback',
  '/admin',
  '/help',
  '/help/changelog',
  '/help/metadata-settings-guide',
  '/help/iptc-keywords-for-stock-photos',
  '/video/stocktag-metadata-studio-demo',
  '/stock-photo-keyword-generator',
  '/stock-photo-keyword-generator-free',
  '/stock-photo-metadata-generator',
  '/stock-photo-tag-generator',
  '/stock-photo-xmp-generator',
  '/stock-photo-csv-generator',
  '/microstock-keyword-generator',
  '/stock-photo-title-generator',
  '/stock-photo-description-generator',
  '/getty-keywords-generator',
  '/adobe-stock-keyword-order',
  '/shutterstock-keywording-tool',
  ...ENGLISH_ONLY_TOOL_LANDING_PATHS
]);

const trimTrailingSlash = (value) => {
  if (!value) return '/';
  const trimmed = String(value).replace(/\/+$/, '');
  return trimmed || '/';
};

const splitPathSuffix = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return { pathname: '/', suffix: '' };

  const protocolMatch = raw.match(/^[a-z]+:\/\/[^/]+(.*)$/i);
  const normalized = protocolMatch ? (protocolMatch[1] || '/') : raw;
  const match = normalized.match(/^([^?#]*)(.*)$/);
  const pathname = match?.[1] || '/';
  const suffix = match?.[2] || '';

  return {
    pathname: trimTrailingSlash(pathname.startsWith('/') ? pathname : `/${pathname}`),
    suffix
  };
};

export const getSupportedSiteLanguages = () => [...SUPPORTED_LANGUAGES];

const matchSupportedLanguage = (value) => {
  if (!value || typeof value !== 'string') return null;
  const normalized = String(value).toLowerCase().split(/[-_]/)[0];
  return SUPPORTED_LANGUAGE_SET.has(normalized) ? normalized : null;
};

export const isSupportedSiteLanguage = (value) => Boolean(matchSupportedLanguage(value));

export const stripLocalePrefix = (value = '/') => {
  const { pathname, suffix } = splitPathSuffix(value);
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = matchSupportedLanguage(segments[0] || '');
  const hasLocalePrefix = Boolean(firstSegment);
  const strippedSegments = hasLocalePrefix ? segments.slice(1) : segments;
  const strippedPath = strippedSegments.length ? `/${strippedSegments.join('/')}` : '/';

  return {
    locale: hasLocalePrefix ? firstSegment : DEFAULT_LANGUAGE,
    pathname: strippedPath,
    suffix,
    hasLocalePrefix
  };
};

export const localizePath = (value = '/', language = DEFAULT_LANGUAGE) => {
  const normalizedLanguage = normalizeLanguage(language);
  const { pathname, suffix } = splitPathSuffix(value);
  const strippedPath = stripLocalePrefix(pathname).pathname;
  const localizedPath = normalizedLanguage === DEFAULT_LANGUAGE
    ? strippedPath
    : (strippedPath === '/' ? `/${normalizedLanguage}` : `/${normalizedLanguage}${strippedPath}`);

  return `${localizedPath}${suffix}`;
};

export const buildLocalizedUrl = (baseUrl, path, language) => {
  const root = String(baseUrl || '').replace(/\/$/, '');
  return `${root}${localizePath(path, language)}`;
};

export const isLocaleRoutedPath = (value = '/') => {
  const strippedPath = stripLocalePrefix(value).pathname;
  if (LOCALE_ROUTED_STATIC_PATHS.has(strippedPath)) return true;
  if (strippedPath.startsWith('/admin/')) return true;
  if (strippedPath.startsWith('/blog/')) return true;
  if (strippedPath.startsWith('/vs/')) return true;
  return false;
};

export const localizeInternalHref = (href, language) => {
  const normalizedHref = String(href || '').trim();
  if (!normalizedHref) return normalizedHref;
  if (/^(?:[a-z]+:)?\/\//i.test(normalizedHref)) return normalizedHref;
  if (/^(?:mailto:|tel:|javascript:)/i.test(normalizedHref)) return normalizedHref;
  if (normalizedHref.startsWith('#')) return normalizedHref;
  return localizePath(normalizedHref, language);
};

const resolveSeoHrefLanguage = (href, language) => {
  const normalizedLanguage = normalizeLanguage(language);
  const { pathname } = stripLocalePrefix(href);

  if (APP_ROUTE_META_BY_PATH[pathname]) {
    const availableLanguages = getAppRouteAvailableLanguages(pathname);
    return availableLanguages.includes(normalizedLanguage) ? normalizedLanguage : DEFAULT_LANGUAGE;
  }

  const routeLanguages = SEO_PATH_LANGUAGE_OVERRIDES.get(pathname);
  if (routeLanguages && !routeLanguages.has(normalizedLanguage)) return DEFAULT_LANGUAGE;

  return normalizedLanguage;
};

export const localizeSeoHref = (href, language) => (
  isLocaleRoutedPath(href)
    ? localizeInternalHref(href, resolveSeoHrefLanguage(href, language))
    : href
);

const BLOG_POST_PATH_PATTERN = /^\/blog\/([^/]+)$/;

// An unprefixed /blog/<slug> URL may only be switched to a language whose
// translation of that post is prerendered. Firestore (BlogBot) posts are
// English-only at the edge: their localized paths would serve the 404 document.
const hasPrerenderedAutoSwitchTarget = (pathname, language) => {
  const blogSlug = pathname.match(BLOG_POST_PATH_PATTERN)?.[1];
  if (!blogSlug) return true;
  return getStaticBlogTranslationLanguages(blogSlug).includes(language);
};

// The language a visitor who prefers `language` should get for the URL `value`.
// The entry middleware redirects to it and the client contexts adopt it, so
// both sides only ever move a visitor from an unprefixed URL to a localized
// URL that is actually prerendered. A URL that already carries a locale prefix
// keeps that locale.
export const resolveLocaleLanguageForPath = (value, language) => {
  const normalizedLanguage = normalizeLanguage(language);
  const parsedPath = stripLocalePrefix(value);
  const normalizedPath = parsedPath.pathname === '/index.html' ? `/${parsedPath.suffix}` : value;
  if (!isLocaleRoutedPath(normalizedPath)) return normalizedLanguage;
  const routedPath = stripLocalePrefix(normalizedPath);
  if (
    !routedPath.hasLocalePrefix
    && normalizedLanguage !== DEFAULT_LANGUAGE
    && !hasPrerenderedAutoSwitchTarget(routedPath.pathname, normalizedLanguage)
  ) {
    return DEFAULT_LANGUAGE;
  }
  return stripLocalePrefix(localizeSeoHref(normalizedPath, normalizedLanguage)).locale;
};

export const buildHrefLangEntries = ({
  baseUrl,
  path,
  languages = [],
  includeXDefault = true
}) => {
  const normalizedLanguages = Array.from(new Set(
    (Array.isArray(languages) ? languages : [])
      .map((language) => matchSupportedLanguage(language))
      .filter(Boolean)
  ));

  const entries = normalizedLanguages.map((language) => ({
    hrefLang: getLanguageMarket(language).hreflang,
    href: buildLocalizedUrl(baseUrl, path, language)
  }));

  if (includeXDefault) {
    entries.push({
      hrefLang: 'x-default',
      href: buildLocalizedUrl(baseUrl, path, DEFAULT_LANGUAGE)
    });
  }

  return entries;
};

const LANGUAGE_LABELS = new Map(
  LANGUAGE_OPTIONS.map((option) => [normalizeLanguage(option.code), option.label])
);

const toSiteRelativeHref = (href = '') => {
  const { pathname, suffix } = splitPathSuffix(href);
  return `${pathname}${suffix}`;
};

// Crawlable links to the other language versions of a page, built from the very
// entries that page puts in its <head> (buildHrefLangEntries), so the visible
// links can never point anywhere the hreflang cluster does not. x-default and
// the current language are left out.
export const buildLanguageAlternateLinks = (hrefLangEntries = [], currentLanguage = DEFAULT_LANGUAGE) => {
  const current = matchSupportedLanguage(currentLanguage) || DEFAULT_LANGUAGE;
  const seen = new Set([current]);

  return (Array.isArray(hrefLangEntries) ? hrefLangEntries : []).reduce((links, entry) => {
    const hrefLang = String(entry?.hrefLang || '').trim();
    if (!hrefLang || hrefLang.toLowerCase() === 'x-default' || !entry?.href) return links;
    const language = matchSupportedLanguage(hrefLang);
    if (!language || seen.has(language)) return links;
    seen.add(language);
    links.push({
      language,
      hrefLang,
      href: toSiteRelativeHref(entry.href),
      label: LANGUAGE_LABELS.get(language) || language
    });
    return links;
  }, []);
};

export const buildOgLocaleAlternates = ({
  languages = [],
  currentLanguage,
  includeCurrent = false
}) => {
  const current = matchSupportedLanguage(currentLanguage);
  const normalizedLanguages = Array.from(new Set(
    (Array.isArray(languages) ? languages : [])
      .map((language) => matchSupportedLanguage(language))
      .filter(Boolean)
  ));

  return normalizedLanguages
    .filter((language) => includeCurrent || language !== current)
    .map((language) => getLanguageMarket(language).ogLocale);
};
